/**
 * Supabase Auth: MFA (TOTP) for the dashboard.
 * Requires MFA enabled in Supabase Dashboard (Authentication → Multi-factor).
 */
import { getSupabaseBrowserClient } from "./supabaseBrowser";
import type { Session } from "@supabase/supabase-js";

/** Routes where an AAL1 session is expected (password step done, TOTP not yet). */
const AUTH_SHELL_PREFIXES = [
  "/login",
  "/register",
  "/verify-email",
  "/forgot-password",
  "/reset-password",
  "/auth/callback",
  "/oauth-complete",
] as const;

export function isAuthShellPathname(pathname: string): boolean {
  return AUTH_SHELL_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

/**
 * On app routes, drop a password-only Supabase session when MFA is still required.
 * Prevents refresh/navigation from using that session with the API (server also enforces AAL2).
 */
export async function clearPartialMfaSessionIfOnAppRoute(): Promise<void> {
  if (typeof window === "undefined") return;
  if (isAuthShellPathname(window.location.pathname)) return;

  const supabase = getSupabaseBrowserClient();
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session) return;

  const { data: aal, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
  if (error) return;

  if (aal.nextLevel === "aal2" && aal.currentLevel === "aal1") {
    await supabase.auth.signOut();
    localStorage.removeItem("sb-access-token");
    localStorage.removeItem("sb-refresh-token");
    sessionStorage.removeItem("sb-access-token");
    sessionStorage.removeItem("sb-refresh-token");
  }
}

export function getStoredSbTokens(): { access: string; refresh: string } | null {
  const access =
    localStorage.getItem("sb-access-token") || sessionStorage.getItem("sb-access-token");
  const refresh =
    localStorage.getItem("sb-refresh-token") || sessionStorage.getItem("sb-refresh-token");
  if (!access?.trim() || !refresh?.trim()) return null;
  return { access, refresh };
}

/** Hydrate the browser Supabase client from tokens Reacho stores after login/sync. */
export async function ensureBrowserSupabaseSession(): Promise<boolean> {
  const supabase = getSupabaseBrowserClient();
  const { data: existing } = await supabase.auth.getSession();
  if (existing.session?.access_token) return true;
  const t = getStoredSbTokens();
  if (!t) return false;
  const { error } = await supabase.auth.setSession({
    access_token: t.access,
    refresh_token: t.refresh,
  });
  return !error;
}

export type PasswordSignInResult =
  | { status: "session"; session: Session }
  | { status: "mfa_required"; factorId: string }
  | { status: "error"; message: string };

/**
 * Password sign-in on the browser so MFA (AAL2) can be completed before syncing cookies via the API.
 */
export async function signInWithPasswordForDashboard(
  email: string,
  password: string
): Promise<PasswordSignInResult> {
  const supabase = getSupabaseBrowserClient();
  await supabase.auth.signOut().catch(() => {});

  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error || !data.session) {
    return {
      status: "error",
      message: error?.message ?? "Invalid email or password",
    };
  }

  const { data: aal, error: aalErr } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
  if (aalErr) {
    await supabase.auth.signOut().catch(() => {});
    return { status: "error", message: aalErr.message };
  }

  if (aal.nextLevel === "aal2" && aal.currentLevel === "aal1") {
    const { data: factors, error: fErr } = await supabase.auth.mfa.listFactors();
    if (fErr || !factors) {
      await supabase.auth.signOut().catch(() => {});
      return { status: "error", message: fErr?.message ?? "Could not load MFA factors" };
    }
    const verified = factors.totp.filter((f) => f.status === "verified");
    const factor = verified[0];
    if (!factor) {
      await supabase.auth.signOut().catch(() => {});
      return {
        status: "error",
        message: "Authenticator is not set up correctly. Contact support.",
      };
    }
    return { status: "mfa_required", factorId: factor.id };
  }

  return { status: "session", session: data.session };
}

export async function completeTotpMfa(factorId: string, code: string): Promise<Session | null> {
  const supabase = getSupabaseBrowserClient();
  const { data: challenge, error: cErr } = await supabase.auth.mfa.challenge({ factorId });
  if (cErr || !challenge?.id) {
    throw new Error(cErr?.message ?? "MFA challenge failed");
  }
  const { error: vErr } = await supabase.auth.mfa.verify({
    factorId,
    challengeId: challenge.id,
    code: code.replace(/\s/g, ""),
  });
  if (vErr) throw new Error(vErr.message);
  const { data: sessionData } = await supabase.auth.getSession();
  return sessionData.session ?? null;
}

export async function listTotpFactors() {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.auth.mfa.listFactors();
  if (error) throw new Error(error.message);
  return data;
}

export async function startTotpEnrollment(friendlyName: string) {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.auth.mfa.enroll({
    factorType: "totp",
    friendlyName: friendlyName.trim() || "Authenticator app",
  });
  if (error || !data?.id || !data.totp) {
    throw new Error(error?.message ?? "Could not start authenticator enrollment");
  }
  return {
    factorId: data.id,
    qrCode: data.totp.qr_code,
    secret: data.totp.secret,
    uri: data.totp.uri,
  };
}

/** Confirm a newly enrolled TOTP factor using the 6-digit code from the authenticator app. */
export async function confirmTotpEnrollment(factorId: string, code: string): Promise<void> {
  const supabase = getSupabaseBrowserClient();
  const { data: challenge, error: cErr } = await supabase.auth.mfa.challenge({ factorId });
  if (cErr || !challenge?.id) {
    throw new Error(cErr?.message ?? "MFA challenge failed");
  }
  const { error: vErr } = await supabase.auth.mfa.verify({
    factorId,
    challengeId: challenge.id,
    code: code.replace(/\s/g, ""),
  });
  if (vErr) throw new Error(vErr.message);
}

export async function unenrollTotpFactor(factorId: string): Promise<void> {
  const supabase = getSupabaseBrowserClient();
  const { error } = await supabase.auth.mfa.unenroll({ factorId });
  if (error) throw new Error(error.message);
}
